import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  request,
  refreshSession,
  keepAliveDue,
  setAccessToken,
  setUnauthorizedHandler,
  ApiError,
  type SessionResponse,
  type Permission,
} from '../api/client';

type Admin = SessionResponse['admin'];

interface AuthState {
  admin: Admin | null;
  /** True until the initial silent refresh settles. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Gate UI on the permission list the API resolved, not on the role name. */
  can: (permission: Permission) => boolean;
  /**
   * Re-read the session now rather than on the next timer tick — after
   * editing a role the signed-in person holds, so `can` reflects it at once.
   */
  refresh: () => Promise<void>;
  /** Set when the session ended on its own — shown on the sign-in screen. */
  endedReason: string | null;
  /**
   * The temporary password an invitee just signed in with, held in memory only
   * so the "choose your password" step need not ask for it again. Null after a
   * reload — that step then asks — and cleared once a password is chosen.
   */
  temporaryPassword: string | null;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Refresh a little before the 15-minute access token lapses, so a long look at
 * one screen does not end in a failed request the user has to retry.
 */
const REFRESH_INTERVAL_MS = 12 * 60_000;

/**
 * The keep-alive's pace (QA C-OTH-012): checked every minute, and at most one
 * GET /auth/me every five, only while someone is at the page and nothing
 * else has gone out that counts (client.ts `keepAliveDue`).
 */
const KEEP_ALIVE_CHECK_MS = 60_000;
const KEEP_ALIVE_EVERY_MS = 5 * 60_000;

const ENDED = 'Your session ended. Please sign in again.';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const clear = useCallback((reason: string | null) => {
    setAccessToken(null);
    setAdmin(null);
    setTemporaryPassword(null);
    // Several requests in flight all find the session over. The first says
    // why ("Signed out after inactivity"); the rest only that it has ended,
    // so a reason already given stands. Signing out by hand clears it.
    setEndedReason((prev) => (reason && prev ? prev : reason));
    if (timer.current) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  // Try the httpOnly refresh cookie on load: a reload should not require a
  // fresh sign-in, but the access token itself is never persisted.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // `refreshSession` rather than a bare request: it is deduped, so
      // StrictMode's double invocation is one round trip, not two racing ones.
      const res = await refreshSession();
      if (cancelled) return;
      if (res) setAdmin(res.admin);
      else setAccessToken(null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Any 401 the client could not recover from ends the session here — a poll
  // that finds the session idled out included — with the API's own reason
  // ("Signed out after inactivity. Sign in again.") on the sign-in screen.
  useEffect(() => {
    setUnauthorizedHandler((reason) => clear(reason ?? ENDED));
    return () => setUnauthorizedHandler(null);
  }, [clear]);

  useEffect(() => {
    if (!admin) return;
    timer.current = window.setInterval(() => {
      // Background: renewing the token on a timer is not somebody using the
      // dashboard, so it must not keep an unattended session alive.
      void refreshSession({ background: true }).then((res) => {
        if (res) setAdmin(res.admin);
        else clear(ENDED);
      });
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [admin, clear]);

  // Someone writing a long update makes no request for minutes at a time, and
  // the API no longer counts the bell or the token refresh as them being
  // here. An ordinary request, sent only because they are typing, scrolling
  // or clicking, keeps them signed in; an untouched tab sends none.
  const signedIn = admin !== null;
  useEffect(() => {
    if (!signedIn) return;
    const id = window.setInterval(() => {
      if (!keepAliveDue(KEEP_ALIVE_EVERY_MS)) return;
      // A 401 here ends the session through the handler above, like any other.
      request('/auth/me', { background: false }).catch(() => undefined);
    }, KEEP_ALIVE_CHECK_MS);
    return () => window.clearInterval(id);
  }, [signedIn]);

  const signIn = useCallback(async (email: string, password: string) => {
    // Trying again answers the "your session ended" note; leaving it up beside
    // a wrong-password message would say two things at once.
    setEndedReason(null);
    // Anonymous: a 401 here means the email and password do not match, not
    // that a token lapsed — so no refresh, and no "session ended".
    const res = await request<SessionResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    });
    setAccessToken(res.accessToken);
    setAdmin(res.admin);
    setTemporaryPassword(res.admin.mustChangePassword ? password : null);
    setEndedReason(null);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await request('/auth/logout', { method: 'POST' });
    } catch {
      // Signing out locally matters more than the round trip succeeding.
    }
    clear(null);
  }, [clear]);

  const can = useCallback(
    (permission: Permission) => admin?.permissions.includes(permission) ?? false,
    [admin],
  );

  const refresh = useCallback(async () => {
    const res = await refreshSession();
    if (res) {
      setAdmin(res.admin);
      if (!res.admin.mustChangePassword) setTemporaryPassword(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ admin, loading, signIn, signOut, can, refresh, endedReason, temporaryPassword }),
    [admin, loading, signIn, signOut, can, refresh, endedReason, temporaryPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export { ApiError };
